import React, { useState } from 'react';
import axios from 'axios';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import { Linkedin, Send, Loader2 } from 'lucide-react';
import { portfolioData } from '../mock';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API_URL}/contact`, formData);
      toast({
        title: "Message Sent!",
        description: response.data?.message || "Thank you for reaching out. I'll get back to you soon.",
      });
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      const errorMessage = error?.response?.data?.detail || "Failed to send message. Please try again or email saloom.info@gmail.com directly.";
      toast({
        title: "Something went wrong",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-32 bg-black border-t border-zinc-900">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="mb-20">
          <span className="text-sm tracking-[0.3em] text-zinc-500 uppercase font-medium mb-4 block">
            Get In Touch
          </span>
          <h2 className="text-5xl md:text-6xl font-black text-white tracking-tight mb-6">
            Let's Create
            <br />
            <span className="text-cyan-400">Together</span>
          </h2>
          <p className="text-xl text-zinc-400 max-w-2xl">
            Have a project in mind? Let's bring your vision to life.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-16">
          {/* Contact Info */}
          <div className="lg:col-span-2 space-y-8">
            <div>
              <div className="text-sm text-zinc-500 uppercase tracking-wider mb-2">Email</div>
              <a 
                href={`mailto:${portfolioData.email}`}
                className="text-xl text-white hover:text-cyan-400 transition-colors font-medium"
              >
                {portfolioData.email}
              </a>
            </div>
            
            <div>
              <div className="text-sm text-zinc-500 uppercase tracking-wider mb-2">Phone</div>
              <a 
                href={`tel:${portfolioData.phone}`}
                className="text-xl text-white hover:text-cyan-400 transition-colors font-medium"
              >
                {portfolioData.phone}
              </a>
            </div>
            
            <div>
              <div className="text-sm text-zinc-500 uppercase tracking-wider mb-2">LinkedIn</div>
              <a 
                href={portfolioData.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xl text-white hover:text-cyan-400 transition-colors font-medium inline-flex items-center gap-2"
              >
                Connect with me
                <Linkedin className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-3">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm text-zinc-500 uppercase tracking-wider mb-3">
                    Your Name
                  </label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Doe"
                    className="bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600 focus:border-white h-12 text-base"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm text-zinc-500 uppercase tracking-wider mb-3">
                    Email
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    className="bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600 focus:border-white h-12 text-base"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm text-zinc-500 uppercase tracking-wider mb-3">
                  Subject
                </label>
                <Input
                  id="subject"
                  name="subject"
                  type="text"
                  required
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="Project Inquiry"
                  className="bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600 focus:border-white h-12 text-base"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm text-zinc-500 uppercase tracking-wider mb-3">
                  Message
                </label>
                <Textarea
                  id="message"
                  name="message"
                  required
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell me about your project..."
                  rows={8}
                  className="bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600 focus:border-white resize-none text-base"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="bg-white hover:bg-zinc-100 text-black font-bold py-6 px-8 transition-all duration-300 hover:scale-105 text-base"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-5 w-5" />
                    SEND MESSAGE
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
